// Inteligencia › Materiales: llegada de material NUEVO a la obra (pallets, bolsones, arena, piedra, hierros,
// maderas, caños), con foto de antes y después. Dos fuentes, sin costo extra:
//  1) Las entregas del modelo nuevo (entregasV3.js: las arma el servicio iaV3 cuando un camión deja material, y las
//     sirve /api/entregas). Se muestran con la misma tarjeta del Calendario de obra (confirmar / descartar queda
//     guardado para reentrenar el modelo).
//  2) Material que aparece de un día para otro en un lugar donde antes no había (acopioData.js), para lo que llegó
//     sin que la cámara viera el camión (descarga fuera de cuadro, de noche o entre dos fotos).
import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { T, Btn, Card, Chip } from '../ui';
import { fmtHM, fmtKey, fmtDow, listDays, addDays, dayKey } from '../iaData';
import { Visit, Photo, Visor } from '../CalendarioObra';
import { cargarEntregas, guardarMarca, titulo as tituloV3 } from '../entregasV3';
import { MATERIALES, MAT, recorte, plural } from './datosV3';
import { useFotosV3, useNarrow, Encabezado, Rango, Nota, Cargando, Kpi, Vacio, SinAnalisis, textoRango } from './comun';
import { nuevoPdf, imagenRecorte, nombreArchivo } from './pdf';

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const lunes = (k) => addDays(k, -((new Date(k + 'T12:00:00Z').getUTCDay() + 6) % 7));
// Solo material MIRADO en el recorte de llegada + después (materialVerif 'ok'); la clase del detector sola no alcanza
// (confunde madera con piedra, cambios de luz, etc.). Una visita sin material confirmado no es una llegada.
const matsDe = (v) => (v.materialVerif === 'ok' ? (v.materiales || []).filter((m) => MAT[m.cls]) : []);

// Zona alrededor de unas cajas (recorte 16:9)
const zonaDe = (p, cajas) => {
  if (!p || !cajas.length) return null;
  const u = { x1: Math.min(...cajas.map((b) => b.x1)), y1: Math.min(...cajas.map((b) => b.y1)), x2: Math.max(...cajas.map((b) => b.x2)), y2: Math.max(...cajas.map((b) => b.y2)) };
  return recorte(p, u, { k: 1.8, ratio: 16 / 9, min: 0.22 });
};
const zonaEvento = (v) => (v.sinCaja ? null : zonaDe({ w: v.w, h: v.h }, [v.box]));

const tituloNuevo = (x) => { const f = MAT[x.c].fem; return `${cap(plural(x.n, `${MAT[x.c].uno} ${f ? 'nueva' : 'nuevo'}`, `${MAT[x.c].corto} ${f ? 'nuevas' : 'nuevos'}`))} a la vista`; };

function TarjetaNuevo({ x, narrow }) {
  const [visor, setVisor] = useState(null);
  const m = MAT[x.c];
  const zona = zonaDe(x.b.foto, x.cajas);
  const fotos = [
    { photo: x.a.foto, label: 'Antes', boxes: [], zoom: zona },
    { photo: x.b.foto, label: 'Ahora', boxes: x.cajas.map((d) => ({ ...d, color: m.color })), zoom: zona },
  ];
  return (
    <div style={{ border: `1px solid ${m.color}44`, borderLeft: `4px solid ${m.color}`, borderRadius: 12, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{m.icon} {tituloNuevo(x)}</div>
        <div style={{ fontSize: 12, color: T.muted }}>entre el {fmtKey(x.a.dia)} y el {fmtKey(x.b.dia)}</div>
      </div>
      <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>No se vio el camión: apareció de un día para otro (pudo descargarse fuera de cuadro, de noche o entre dos fotos).</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 10, maxWidth: narrow ? '100%' : 620 }}>
        {fotos.map((f, i) => (
          <div key={f.label} style={{ minWidth: 0 }}>
            {f.photo ? <Photo photo={f.photo} crop={zona} boxes={f.boxes} onClick={() => setVisor(i)} style={{ cursor: 'zoom-in' }} /> : <div style={{ aspectRatio: '16 / 9', background: '#f3f4f6', borderRadius: 8 }} />}
            <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{f.label}{f.photo ? ` · ${fmtKey(dayKey(f.photo.ms))} ${fmtHM(f.photo.ms)}` : ''}</div>
          </div>
        ))}
      </div>
      {visor != null && <Visor fotos={fotos} inicio={visor} zoomZona={zona} onClose={() => setVisor(null)} />}
    </div>
  );
}

async function generarPdf({ name, rango, items }) {
  const pdf = nuevoPdf({
    titulo: 'Llegada de materiales', obra: name, periodo: textoRango(rango),
    nota: 'Material nuevo que apareció en la zona que ve la cámara (foto de antes y de después). Lo que se descarga fuera de cuadro o queda tapado no se ve, y el tipo de material es aproximado. Generado automáticamente a partir de las fotos de la cámara.',
  });
  const tot = {};
  items.forEach((it) => it.clases.forEach((c) => { tot[c] = (tot[c] || 0) + 1; }));
  pdf.titulo('Resumen');
  pdf.tabla([{ t: 'Material', w: 110 }, { t: 'Llegadas', w: 72 }], Object.entries(tot).sort((a, b) => b[1] - a[1]).map(([c, n]) => [MAT[c].label, String(n)]));
  let diaActual = null;
  for (const it of items) {
    if (it.dia !== diaActual) { diaActual = it.dia; pdf.titulo(`${cap(fmtDow(it.dia))} ${fmtKey(it.dia)}`); }
    let antes, despues, zona;
    if (it.v) {
      const v = it.v;
      pdf.texto(`${fmtHM(v.arrive)} h · ${tituloV3(v)}${it.estado === 'si' ? ' (confirmada)' : ''}`, { negrita: true });
      antes = v.before; despues = v.after || v.arrival; zona = zonaEvento(v);
    } else {
      pdf.texto(`${tituloNuevo(it.x)} (apareció de un día para otro, sin camión a la vista)`, { negrita: true });
      antes = it.x.a.foto; despues = it.x.b.foto; zona = zonaDe(it.x.b.foto, it.x.cajas);
    }
    const cajas = it.x ? it.x.cajas.map((d) => ({ ...d, color: MAT[it.x.c].color })) : [];
    // eslint-disable-next-line no-await-in-loop
    const [a, b] = await Promise.all([antes ? imagenRecorte(antes, zona, [], 420) : null, despues ? imagenRecorte(despues, zona, cajas, 420) : null]);
    // eslint-disable-next-line no-await-in-loop
    await pdf.imagenes([
      { img: a, leyenda: antes ? `Antes · ${fmtKey(dayKey(antes.ms))} ${fmtHM(antes.ms)}` : 'Antes' },
      { img: b, leyenda: despues ? `Después · ${fmtKey(dayKey(despues.ms))} ${fmtHM(despues.ms)}` : 'Después' },
    ], { alto: 44 });
  }
  pdf.guardar(nombreArchivo('materiales', name, rango));
}

export default function Materiales({ feedId, name }) {
  const narrow = useNarrow();
  const { rango, setRango, cargando, fotos, fuente, analizadoDesde } = useFotosV3(feedId, 14);
  const [datos, setDatos] = useState(null);
  const [tipo, setTipo] = useState(null);
  const [marcas, setMarcas] = useState({});
  const [haciendoPdf, setHaciendoPdf] = useState(false);
  useEffect(() => {
    let vivo = true;
    setDatos(null);
    cargarEntregas(feedId, rango.desde, rango.hasta).then((r) => vivo && setDatos(r));
    return () => { vivo = false; };
  }, [feedId, rango.desde, rango.hasta]);

  const estadoDe = (v) => (marcas[v.id] || v.marca || {}).estado;
  // Todas las llegadas: entregas con camión + material nuevo de un día para otro (sin repetir la misma clase el mismo día)
  const items = useMemo(() => {
    const out = [];
    if (datos) for (const vs of datos.porDia.values()) for (const v of vs) if (matsDe(v).length) out.push({ key: v.id, dia: v.day, ms: v.arrive, v, clases: [...new Set(matsDe(v).map((m) => m.cls))] });
    // Ya no se suma "material nuevo de un día para otro" sin camión: era frágil (luz, el detector que no lo vio antes).
    return out.sort((a, b) => a.ms - b.ms);
  }, [datos]);
  const vivos = items.filter((it) => !it.v || estadoDe(it.v) !== 'no');
  const visibles = items.filter((it) => !tipo || it.clases.includes(tipo));
  const tipos = MATERIALES.filter((c) => items.some((it) => it.clases.includes(c)));

  // Gráfico: por día si el rango es corto, por semana si es largo. Los días anteriores a que se activara el
  // análisis no se grafican (no son "cero llegadas": no se miró).
  const desdeReal = analizadoDesde && dayKey(analizadoDesde) > rango.desde ? dayKey(analizadoDesde) : rango.desde;
  const serie = useMemo(() => {
    const dias = desdeReal <= rango.hasta ? listDays(desdeReal, rango.hasta) : [];
    const porSemana = dias.length > 21;
    const claves = porSemana ? [...new Set(dias.map(lunes))] : dias;
    const filas = new Map(claves.map((k) => [k, { k, label: porSemana ? `sem ${k.slice(8)}/${k.slice(5, 7)}` : `${fmtDow(k)} ${k.slice(8)}` }]));
    for (const it of vivos) {
      const f = filas.get(porSemana ? lunes(it.dia) : it.dia);
      if (f) it.clases.forEach((c) => { f[c] = (f[c] || 0) + 1; });
    }
    return { porSemana, filas: [...filas.values()] };
  }, [vivos, desdeReal, rango.hasta]);

  const marcar = (v, patch) => {
    const actual = { ...(v.marca || {}), ...(marcas[v.id] || {}) };
    const nueva = { estado: actual.estado ?? null, nota: actual.nota || '', ...patch };
    setMarcas((m) => ({ ...m, [v.id]: nueva }));
    guardarMarca(feedId, v.id, nueva).catch(() => {});
  };
  const pdf = async () => {
    setHaciendoPdf(true);
    try { await generarPdf({ name, rango, items: visibles.filter((it) => !it.v || estadoDe(it.v) !== 'no').map((it) => ({ ...it, estado: it.v ? estadoDe(it.v) : null })) }); } finally { setHaciendoPdf(false); }
  };

  const diasCon = new Set(vivos.map((it) => it.dia)).size;
  const top = MATERIALES.map((c) => [c, vivos.filter((it) => it.clases.includes(c)).length]).sort((a, b) => b[1] - a[1])[0];
  let diaActual = null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Encabezado icono="📦" titulo="Materiales" obra={name} bajada="Material nuevo que llegó a la obra, con foto de antes y después">
        <Rango rango={rango} setRango={setRango} />
      </Encabezado>
      {cargando || !datos ? <Card><Cargando /></Card> : fuente !== 'iaV3' && datos.fuente !== 'iaV3' ? <Card><SinAnalisis /></Card> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${narrow ? 2 : 4}, minmax(0, 1fr))`, gap: 10 }}>
            <Kpi label="Llegadas de material" valor={vivos.length} sub={desdeReal > rango.desde ? `analizado desde el ${fmtKey(desdeReal)}` : textoRango(rango)} />
            <Kpi label="Visitas con material" valor={vivos.length} sub="mirado en la foto de llegada y la de después" />
            <Kpi label="Días con llegadas" valor={diasCon} />
            <Kpi label="Lo que más llegó" valor={top && top[1] ? `${MAT[top[0]].icon} ${cap(MAT[top[0]].corto)}` : '—'} sub={top && top[1] ? plural(top[1], 'llegada', 'llegadas') : null} />
          </div>
          <Card title={<span>Llegadas por {serie.porSemana ? 'semana' : 'día'}</span>} right={<Btn onClick={pdf} disabled={haciendoPdf || !visibles.length}>{haciendoPdf ? 'Armando PDF…' : '⬇ Descargar PDF'}</Btn>}>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={serie.filas} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.line} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: T.muted }} interval="preserveStartEnd" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: T.muted }} />
                  <Tooltip formatter={(val, key) => [val, MAT[key]?.label || key]} />
                  {MATERIALES.map((c) => <Bar key={c} dataKey={c} stackId="m" fill={MAT[c].color} name={MAT[c].label} isAnimationActive={false} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              <Chip on={!tipo} onClick={() => setTipo(null)}>Todos</Chip>
              {tipos.map((c) => <Chip key={c} color={MAT[c].color} on={tipo === c} onClick={() => setTipo(tipo === c ? null : c)}>{MAT[c].icon} {MAT[c].label}</Chip>)}
            </div>
          </Card>
          <Card title={<span>Llegadas ({visibles.length})</span>}>
            {!visibles.length ? <Vacio>No se vio material nuevo en el {textoRango(rango)}.</Vacio> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {visibles.map((it) => {
                  const cab = it.dia !== diaActual ? (diaActual = it.dia) : null;
                  return (
                    <React.Fragment key={it.key}>
                      {cab && <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginTop: 4 }}>{cap(fmtDow(cab))} {fmtKey(cab)}</div>}
                      {it.v ? <Visit v={it.v} mark={marcas[it.v.id] || it.v.marca || null} onMark={marcar} /> : <TarjetaNuevo x={it.x} narrow={narrow} />}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </Card>
          <Nota>
            Una llegada es un vehículo que trae material a la obra: se ve en la foto de llegada (recorte ampliado) y en la de después, y el tipo de material sale de mirar
            esas fotos, no solo del detector. Lo que aparece de un día para otro sin un vehículo a la vista no se cuenta. Lo que se descarga fuera de cuadro, detrás de algo
            o muy lejos no se ve. Confirmá o descartá cada entrega: eso mejora el reconocimiento.
          </Nota>
        </>
      )}
    </div>
  );
}
